import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, ArrowRight } from 'lucide-react';
import { SHOP_CATEGORIES } from '@meshsmith/shared';

interface SearchOverlayProps {
  open: boolean;
  query: string;
  onQueryChange: (q: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}

/**
 * Spotlight-style search: a floating panel anchored under the header rather
 * than a full-width strip, with a dimmed backdrop, a pill input, and quick
 * category jumps when there's no query yet.
 */
export function SearchOverlay({ open, query, onQueryChange, onSubmit, onClose }: SearchOverlayProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-charcoal/30 backdrop-blur-[1px]" onClick={onClose} aria-hidden />
      <div className="absolute inset-x-0 top-full z-50 flex justify-center px-4">
        <div className="pop-in card mt-3 w-full max-w-xl overflow-hidden p-2 shadow-lg">
          <form onSubmit={onSubmit} role="search" className="flex items-center gap-2">
            <div
              className="flex flex-1 items-center gap-2.5 rounded-full border bg-warm-off-white px-4 py-2.5 transition-colors focus-within:border-deep-teal focus-within:bg-white"
              style={{ borderColor: 'var(--border)' }}
            >
              <Search size={17} className="shrink-0 text-secondary-grey" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                placeholder="Search products, SKUs, materials…"
                className="w-full bg-transparent text-sm outline-none placeholder:text-secondary-grey"
                aria-label="Search products"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => onQueryChange('')}
                  aria-label="Clear search"
                  className="shrink-0 text-secondary-grey hover:text-charcoal"
                >
                  <X size={15} />
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={!query.trim()}
              aria-label="Search"
              className="grid h-[42px] w-[42px] shrink-0 place-items-center rounded-full text-white transition-opacity disabled:opacity-40"
              style={{ background: 'var(--mesh-blue)' }}
            >
              <ArrowRight size={17} />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close search"
              className="btn-ghost hidden shrink-0 rounded-full p-2.5 sm:inline-flex"
            >
              <X size={18} />
            </button>
          </form>

          {!query && (
            <div className="px-2 pb-1 pt-4">
              <p className="mb-2 px-1 text-[0.7rem] font-bold uppercase tracking-wide text-secondary-grey">
                Popular categories
              </p>
              <div className="flex flex-wrap gap-1.5">
                {SHOP_CATEGORIES.slice(1).map((c) => (
                  <Link
                    key={c.to}
                    to={c.to}
                    onClick={onClose}
                    className="rounded-full border bg-warm-off-white px-3 py-1.5 text-[0.8rem] font-medium text-charcoal transition-colors hover:border-deep-teal hover:bg-pale-aqua"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
